import React from 'react';
import { Mail, Send, Circle, RefreshCw, Download } from 'lucide-react';
import { TemplatePicker } from './AdminTemplatesManager';

const AdminMessagesTab = ({
  messages,
  selectedMessage,
  setSelectedMessage,
  showCompose,
  setShowCompose,
  composeData,
  setComposeData,
  sending,
  handleSendEmail,
  handleReply,
  applyTemplate,
  fetchMessages,
  handleExportMessagesCSV,
}) => {
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      {/* Messages List */}
      <div className="lg:col-span-1 bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="p-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
          <h2 className="font-bold text-gray-900">Posteingang ({messages.length})</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportMessagesCSV}
              disabled={messages.length === 0}
              data-testid="export-messages-csv-btn"
              className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-3.5 h-3.5" />
              CSV
            </button>
            <button
              onClick={fetchMessages}
              data-testid="refresh-messages-btn"
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="overflow-y-auto max-h-[calc(100vh-350px)]">
          {messages.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Mail className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Keine Nachrichten vorhanden</p>
            </div>
          ) : (
            messages.map((message) => (
              <button
                key={message.id}
                onClick={() => { setSelectedMessage(message); setShowCompose(false); }}
                className={`w-full p-4 border-b border-gray-200 hover:bg-blue-50 transition-colors text-left ${
                  selectedMessage?.id === message.id ? 'bg-blue-50' : ''
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <span className="font-semibold text-gray-900 truncate flex-1">{message.name}</span>
                  {message.status === 'new' && (
                    <Circle className="w-3 h-3 text-blue-600 fill-current flex-shrink-0 mt-1" />
                  )}
                </div>
                <p className="text-sm font-medium text-gray-700 truncate mb-1">{message.subject}</p>
                <p className="text-xs text-gray-500 truncate">{message.email}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {new Date(message.submitted_at).toLocaleDateString('de-DE')}
                </p>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Message Detail / Compose */}
      <div className="lg:col-span-2 bg-white rounded-xl shadow-lg p-6">
        {showCompose ? (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Neue E-Mail</h2>
              <TemplatePicker onSelect={(t) => applyTemplate(t, '', composeData.to)} />
            </div>
            <form onSubmit={handleSendEmail} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">An</label>
                <input
                  type="email"
                  value={composeData.to}
                  onChange={(e) => setComposeData({ ...composeData, to: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="empfaenger@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Betreff</label>
                <input
                  type="text"
                  value={composeData.subject}
                  onChange={(e) => setComposeData({ ...composeData, subject: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="E-Mail Betreff"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nachricht</label>
                <textarea
                  value={composeData.message}
                  onChange={(e) => setComposeData({ ...composeData, message: e.target.value })}
                  required
                  rows={12}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Ihre Nachricht..."
                />
              </div>
              <div className="flex gap-4">
                <button
                  type="submit"
                  disabled={sending}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  {sending ? 'Wird gesendet...' : 'E-Mail senden'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowCompose(false); setComposeData({ to: '', subject: '', message: '' }); }}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        ) : selectedMessage ? (
          <div>
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedMessage.subject}</h2>
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span>Von: <strong>{selectedMessage.name}</strong></span>
                  <span>{selectedMessage.email}</span>
                  <span>{new Date(selectedMessage.submitted_at).toLocaleString('de-DE')}</span>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg mb-6">
              <p className="whitespace-pre-wrap text-gray-700">{selectedMessage.message}</p>
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => handleReply(selectedMessage)}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all hover:scale-105 flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                Antworten
              </button>
              <TemplatePicker onSelect={(t) => {
                applyTemplate(t, selectedMessage?.name, selectedMessage?.email);
              }} />
              <button
                onClick={() => setSelectedMessage(null)}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
              >
                Schliessen
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center py-20 text-gray-500">
            <Mail className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p>Waehlen Sie eine Nachricht aus oder erstellen Sie eine neue E-Mail</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMessagesTab;
